import React, { useEffect, useState } from "react";
import { getUserChats } from "../components/utils/chatApi";
import ChatRoomPage from "./ChatRoomPage";
import { useLocation } from "react-router-dom";

async function markAllMessagesAsRead(chatRoomId) {
  const tokenObj = JSON.parse(localStorage.getItem('auth-storage'));
  const token = tokenObj?.state?.accessToken;
  await fetch(`/v1/chat/readAll/${chatRoomId}`, {
    method: "PUT",
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
}

const ChatListPage = () => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const username = user?.username;

  const location = useLocation();
  const openChatRoomId = location.state?.openChatRoomId;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setSidebarOpen(false);
      else setSidebarOpen(true);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (username) {
      getUserChats(username).then(setChats);
    }
  }, [username]);

  useEffect(() => {
    if (chats.length > 0) {
      let autoSelect = null;
      if (openChatRoomId) {
        autoSelect = chats.find(c => String(c.chatRoomId) === String(openChatRoomId));
      }
      setSelectedChat(autoSelect || selectedChat || null);
      if (openChatRoomId) {
        window.history.replaceState({}, document.title);
      }
    }
    // eslint-disable-next-line
  }, [chats, openChatRoomId]);

  // Sidebar'dan seçim:
  const handleChatClick = async (chat) => {
    setChats(oldChats =>
      oldChats.map(c =>
        c.chatRoomId === chat.chatRoomId ? { ...c, unreadCount: 0 } : c
      )
    );
    await markAllMessagesAsRead(chat.chatRoomId);
    setSelectedChat(chat);
    if (window.innerWidth < 768) setSidebarOpen(false);
  };

  const isDark = document.body.classList.contains("dark");

  return (
    <div className="h-screen w-full flex overflow-hidden">
      {!sidebarOpen && (
        <button
          className="fixed left-2 top-1/2 -translate-y-1/2 z-50 md:hidden bg-sky-600 hover:bg-sky-700 text-white p-2 rounded-full shadow-lg transition"
          style={{
            width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => setSidebarOpen(true)}
          aria-label="Sohbet listesini aç"
        >
          <svg width={24} height={24} fill="none" viewBox="0 0 24 24">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed z-40 top-0 left-0 h-full w-[80vw] max-w-xs md:static md:w-[340px]
          transition-transform duration-300
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          ${isDark ? "bg-gray-950 border-gray-900" : "bg-white border-gray-100"}
          border-r shadow-lg
        `}
        style={{
          minWidth: 220,
        }}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-950/80">
          <h2 className={`text-2xl font-bold tracking-tight ${isDark ? "text-sky-200" : "text-sky-700"}`}>Sohbetler</h2>
          <button
            className="md:hidden p-1 rounded hover:bg-sky-50 dark:hover:bg-gray-900"
            onClick={() => setSidebarOpen(false)}
          >
            <svg width={28} height={28} fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M6 6l12 12M6 18L18 6"/>
            </svg>
          </button>
        </div>
        <div className="overflow-y-auto h-[calc(100vh-72px)] px-2 py-2">
          {chats.length === 0 ? (
            <div className="text-center p-8 text-gray-400 dark:text-sky-300 text-base">Hiç mesaj yok.</div>
          ) : (
            <ul className="space-y-1">
              {chats.map(chat => {
                const isUnread = chat.unreadCount > 0;
                const isSelected = selectedChat?.chatRoomId === chat.chatRoomId;
                return (
                  <li key={chat.chatRoomId}>
                    <button
                      onClick={() => handleChatClick(chat)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-left relative group shadow-sm
                        ${isSelected
                          ? isDark ? "bg-sky-900/60 text-sky-100 border border-sky-700" : "bg-sky-50 text-sky-800 border border-sky-200"
                          : isDark ? "bg-gray-900 text-sky-100 hover:bg-gray-800/80" : "bg-white text-sky-700 hover:bg-sky-50"
                        }
                        ${isUnread ? "ring-2 ring-sky-400 dark:ring-sky-700" : ""}
                      `}
                      style={{ minHeight: 64 }}
                    >
                      {chat.profileImageUrl ? (
                        <img src={chat.profileImageUrl} alt={chat.displayName} className="h-11 w-11 rounded-full object-cover border-2 border-sky-200 dark:border-sky-800" />
                      ) : (
                        <div className={`h-11 w-11 rounded-full flex items-center justify-center bg-sky-100 dark:bg-sky-900 border-2 border-sky-200 dark:border-sky-800`}>
                          <span className={`text-xl font-bold text-sky-600 dark:text-sky-200`}>{chat.displayName?.[0]?.toUpperCase()}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`truncate font-semibold text-base ${isUnread ? "text-sky-700 dark:text-sky-200" : "text-gray-800 dark:text-sky-100"}`}>{chat.displayName}</div>
                        <div className={`truncate text-sm ${isUnread ? "font-medium text-sky-500 dark:text-sky-400" : "text-gray-400 dark:text-gray-500"}`}>{chat.lastMessage}</div>
                      </div>
                      {isUnread && (
                        <span className="ml-2 px-2 py-0.5 rounded-full bg-gradient-to-r from-rose-400 to-rose-600 text-white text-xs font-bold shadow animate-pulse">
                          {chat.unreadCount}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {sidebarOpen && window.innerWidth < 768 && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
        {selectedChat ? (
          <ChatRoomPage
            key={selectedChat.chatRoomId}
            chatRoomId={selectedChat.chatRoomId}
            receiverUsername={selectedChat.displayUsername || selectedChat.displayName}
            chatRoomType={selectedChat.chatRoomType}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500 dark:text-sky-200">
            Sohbet başlatmak için soldan bir kişi seçin
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatListPage;